import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Button, Paper, Stack, TextField, MenuItem,
    FormControlLabel, Switch, CircularProgress, IconButton, Chip,
    Select, OutlinedInput, FormControl, InputLabel, Snackbar, Alert,
    Fade, Tooltip
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CampaignIcon from '@mui/icons-material/Campaign';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';

const API_URL = process.env.REACT_APP_API_URL1;

const AnnouncementEditor = ({ setIsLoggedIn }) => {
    const { id } = useParams();
    const token = useMemo(() => sessionStorage.getItem('token'), []);
    const tenant = useMemo(() => localStorage.getItem('tenant'), []);
    const navigate = useNavigate();
    const config = useMemo(() => ({
        headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant': tenant
        }
    }), [token, tenant]);

    const [loading, setLoading] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        cover_image: '',
        attachment_url: '',
        attachments: [],
        priority: 'NORMAL',
        is_pinned: false,
        status: 'DRAFT',
        publish_date: new Date().toISOString().split('T')[0],
        expiry_date: '',
        require_acknowledgement: false,
        target_type: 'GLOBAL',
        target_ids: []
    });

    const fetchLookups = useCallback(async () => {
        try {
            const [bRes, dRes] = await Promise.all([
                axios.get(`${API_URL}/som/branches`, config),
                axios.get(`${API_URL}/som/nodes`, config)
            ]);
            if (bRes.data?.success) setBranches(bRes.data.data);
            if (dRes.data?.success) setDepartments(buildHierarchicalList(dRes.data.data));
        } catch (err) {
            console.error('Failed to fetch lookups', err);
        }
    }, [config]);

    const fetchAnnouncement = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/sm/announcements/admin`, config);
            if (res.data.success) {
                const item = res.data.data.find(a => a.id === parseInt(id));
                if (item) {
                    let atts = [];
                    if (Array.isArray(item.attachments)) {
                        atts = item.attachments;
                    } else if (typeof item.attachments === 'string' && item.attachments.trim()) {
                        try { atts = JSON.parse(item.attachments); } catch { atts = []; }
                    }
                    if (atts.length === 0 && item.attachment_url) {
                        atts = [{ dms_id: item.attachment_url, name: 'Attachment 1' }];
                    }

                    const parsed = {
                        ...item,
                        attachments: atts,
                        publish_date: item.publish_date ? item.publish_date.split('T')[0] : '',
                        expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : '',
                        target_ids: typeof item.target_ids === 'string' ? JSON.parse(item.target_ids) : (item.target_ids || [])
                    };
                    setFormData(parsed);

                    // Initialize rich text editor with existing HTML content
                    if (item.content) {
                        const blocksFromHtml = htmlToDraft(item.content);
                        const contentState = ContentState.createFromBlockArray(
                            blocksFromHtml.contentBlocks,
                            blocksFromHtml.entityMap
                        );
                        setEditorState(EditorState.createWithContent(contentState));
                    }
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [id, config]);

    useEffect(() => {
        fetchLookups();
        fetchAnnouncement();
    }, [fetchLookups, fetchAnnouncement]);

    const handleChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    const handleEditorChange = useCallback((state) => {
        setEditorState(state);
        const html = draftToHtml(convertToRaw(state.getCurrentContent()));
        setFormData(prev => ({ ...prev, content: html }));
    }, []);

    const handleFileUpload = useCallback(async (e, type) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        if (type === 'image') setUploadingImage(true);
        else setUploadingDoc(true);

        try {
            if (type === 'image') {
                const file = files[0];
                const fd = new FormData();
                fd.append('file', file);
                fd.append('module', 'announcements');

                const res = await axios.post(`${API_URL}/admin/documents`, fd, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'X-Tenant': tenant,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (res.data.success) {
                    setFormData(prev => ({ ...prev, cover_image: res.data.dms_id }));
                    setSnackbar({ open: true, message: 'Cover image uploaded successfully!', severity: 'success' });
                }
            } else {
                const uploadedAtts = [];
                for (const file of files) {
                    const fd = new FormData();
                    fd.append('file', file);
                    fd.append('module', 'announcements');

                    const res = await axios.post(`${API_URL}/admin/documents`, fd, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'X-Tenant': tenant,
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    if (res.data.success) {
                        uploadedAtts.push({
                            dms_id: res.data.dms_id,
                            name: file.name
                        });
                    }
                }

                if (uploadedAtts.length > 0) {
                    setFormData(prev => ({
                        ...prev,
                        attachments: [...(prev.attachments || []), ...uploadedAtts],
                        attachment_url: prev.attachments?.length ? prev.attachment_url : uploadedAtts[0].dms_id
                    }));
                    setSnackbar({ open: true, message: `${uploadedAtts.length} attachment(s) uploaded successfully!`, severity: 'success' });
                }
            }
        } catch (err) {
            console.error('Upload failed', err);
            setSnackbar({ open: true, message: 'Upload failed. Please try again.', severity: 'error' });
        } finally {
            if (type === 'image') setUploadingImage(false);
            else setUploadingDoc(false);
        }
    }, [token, tenant]);

    const handleRemoveAttachment = useCallback((indexToRemove) => {
        setFormData(prev => {
            const nextAtts = (prev.attachments || []).filter((_, i) => i !== indexToRemove);
            return {
                ...prev,
                attachments: nextAtts,
                attachment_url: nextAtts[0]?.dms_id || ''
            };
        });
    }, []);

    const handleSave = useCallback(async () => {
        if (!formData.title) {
            setSnackbar({ open: true, message: 'Title is required.', severity: 'error' });
            return;
        }
        setLoading(true);
        try {
            const payload = { ...formData };
            if (!payload.expiry_date) payload.expiry_date = null;

            if (id) {
                await axios.put(`${API_URL}/sm/announcements/${id}`, payload, config);
            } else {
                await axios.post(`${API_URL}/sm/announcements`, payload, config);
            }
            setSnackbar({ open: true, message: 'Announcement saved!', severity: 'success' });
            setTimeout(() => navigate('/announcements'), 1000);
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save announcement.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [formData, id, config, navigate]);

    const priorityColor = { NORMAL: '#64748b', HIGH: '#f59e0b', URGENT: '#ef4444' };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={() => navigate('/login')} />

            <Fade in timeout={500}>
                <Box sx={{ p: 4, maxWidth: 960, mx: 'auto', width: '100%', flexGrow: 1 }}>
                    {/* Page Header */}
                    <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                        <Tooltip title="Back to Announcements">
                            <IconButton onClick={() => navigate('/announcements')} sx={{ bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <CampaignIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    {id ? 'Edit Announcement' : 'New Announcement'}
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.5, ml: 6 }}>
                                {id ? 'Update the announcement details below.' : 'Create a new company-wide or targeted announcement.'}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={3}>
                        {/* Main Content Card */}
                        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    Announcement Content
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <TextField
                                        label="Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleChange}
                                        fullWidth
                                        required
                                        placeholder="e.g. Important Update for All Staff"
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#475569', fontWeight: 600 }}>
                                            Content (Rich Text)
                                        </Typography>
                                        <Box sx={{
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            '& .rdw-editor-wrapper': { border: 'none' },
                                            '& .rdw-editor-toolbar': { borderBottom: '1px solid #e2e8f0', mb: 0, bgcolor: '#f8fafc' },
                                            '& .rdw-editor-main': { minHeight: 200, px: 2 }
                                        }}>
                                            <Editor
                                                editorState={editorState}
                                                onEditorStateChange={handleEditorChange}
                                                toolbar={{
                                                    options: ['inline', 'blockType', 'list', 'link', 'history'],
                                                    inline: { options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                                    blockType: { inDropdown: true, options: ['Normal', 'H1', 'H2', 'H3', 'Blockquote'] },
                                                    list: { options: ['unordered', 'ordered'] },
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Media Uploads Card */}
                        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    Media &amp; Attachments
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    Add a cover image and optional policy/form attachments for staff to download.
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                                    <Box sx={{ flex: 1, p: 3, border: '1px dashed #cbd5e1', borderRadius: 3, textAlign: 'center', bgcolor: '#fafbfc' }}>
                                        <CloudUploadIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 1 }} />
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Cover Image</Typography>
                                        <Typography variant="caption" display="block" color="text.secondary" mb={2}>
                                            Displays as thumbnail in mobile feed
                                        </Typography>
                                        {formData.cover_image && (
                                            <Chip label={`Uploaded ✓`} color="success" size="small" sx={{ mb: 1.5, display: 'block' }} />
                                        )}
                                        <Button component="label" variant="outlined" size="small"
                                            startIcon={uploadingImage ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                            disabled={uploadingImage}
                                            sx={{ borderRadius: 2, textTransform: 'none' }}>
                                            {uploadingImage ? 'Uploading...' : formData.cover_image ? 'Change Image' : 'Upload Image'}
                                            <input type="file" hidden accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} />
                                        </Button>
                                    </Box>
                                    <Box sx={{ flex: 1, p: 3, border: '1px dashed #cbd5e1', borderRadius: 3, bgcolor: '#fafbfc' }}>
                                        <Box textAlign="center" mb={2}>
                                            <CloudUploadIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 1 }} />
                                            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>Attachments</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Attach policy PDFs, forms, or documents
                                            </Typography>
                                        </Box>

                                        {formData.attachments && formData.attachments.length > 0 && (
                                            <Stack spacing={1} mb={2}>
                                                {formData.attachments.map((att, idx) => (
                                                    <Stack key={att.dms_id || idx} direction="row" alignItems="center" justifyContent="space-between"
                                                        sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff' }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ overflow: 'hidden' }}>
                                                            <InsertDriveFileIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                                            <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: '#334155', maxWidth: 220 }}>
                                                                {att.name || `Attachment ${idx + 1}`}
                                                            </Typography>
                                                        </Stack>
                                                        <IconButton size="small" onClick={() => handleRemoveAttachment(idx)} sx={{ color: '#ef4444' }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        )}

                                        <Box textAlign="center">
                                            <Button component="label" variant="outlined" size="small"
                                                startIcon={uploadingDoc ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                                disabled={uploadingDoc}
                                                sx={{ borderRadius: 2, textTransform: 'none' }}>
                                                {uploadingDoc ? 'Uploading...' : '+ Upload Files'}
                                                <input type="file" hidden multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={(e) => handleFileUpload(e, 'doc')} />
                                            </Button>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Publishing Settings Card */}
                        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    Publishing Settings
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField select label="Status" name="status" value={formData.status} onChange={handleChange} fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                            <MenuItem value="DRAFT">Draft</MenuItem>
                                            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                                            <MenuItem value="PUBLISHED">Published</MenuItem>
                                            <MenuItem value="ARCHIVED">Archived</MenuItem>
                                        </TextField>
                                        <TextField select label="Priority" name="priority" value={formData.priority} onChange={handleChange} fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                            <MenuItem value="NORMAL"><Box component="span" sx={{ color: priorityColor.NORMAL }}>● </Box>Normal</MenuItem>
                                            <MenuItem value="HIGH"><Box component="span" sx={{ color: priorityColor.HIGH }}>● </Box>High</MenuItem>
                                            <MenuItem value="URGENT"><Box component="span" sx={{ color: priorityColor.URGENT }}>● </Box>Urgent</MenuItem>
                                        </TextField>
                                    </Stack>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField type="date" label="Publish Date" name="publish_date" value={formData.publish_date}
                                            onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                        <TextField type="date" label="Expiry Date (Optional)" name="expiry_date" value={formData.expiry_date}
                                            onChange={handleChange} InputLabelProps={{ shrink: true }} fullWidth
                                            helperText="Leave blank for no expiry"
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                                    </Stack>

                                    <Stack direction="row" spacing={4}>
                                        <FormControlLabel
                                            control={<Switch checked={!!formData.is_pinned} onChange={handleChange} name="is_pinned"
                                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' } }} />}
                                            label={<Typography variant="body2" fontWeight={600}>Pin to Top</Typography>}
                                        />
                                        <FormControlLabel
                                            control={<Switch checked={!!formData.require_acknowledgement} onChange={handleChange} name="require_acknowledgement"
                                                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' } }} />}
                                            label={<Typography variant="body2" fontWeight={600}>Require Acknowledgement</Typography>}
                                        />
                                    </Stack>
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Targeting Card */}
                        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                            <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    Audience Targeting
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    Choose who will receive this announcement.
                                </Typography>
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={2}>
                                    <TextField select label="Target Audience" name="target_type" value={formData.target_type}
                                        onChange={(e) => setFormData(prev => ({ ...prev, target_type: e.target.value, target_ids: [] }))}
                                        fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                                        <MenuItem value="GLOBAL">🌐 Global — All Employees</MenuItem>
                                        <MenuItem value="DEPARTMENT">🏢 Specific Department(s)</MenuItem>
                                        <MenuItem value="BRANCH">🏬 Specific Branch(es)</MenuItem>
                                    </TextField>

                                    {formData.target_type === 'DEPARTMENT' && (
                                        <FormControl fullWidth>
                                            <InputLabel>Select Departments</InputLabel>
                                            <Select multiple value={formData.target_ids}
                                                onChange={(e) => setFormData(prev => ({ ...prev, target_ids: e.target.value }))}
                                                input={<OutlinedInput label="Select Departments" />}
                                                sx={{ borderRadius: 2 }}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const dept = departments.find(d => d.id === value);
                                                            return <Chip key={value} label={dept ? dept.node_name : value} size="small"
                                                                sx={{ bgcolor: '#eef2ff', color: 'primary.main' }} />;
                                                        })}
                                                    </Box>
                                                )}>
                                                {departments.map((dept) => (
                                                    <MenuItem key={dept.id} value={dept.id} sx={{ pl: 2 + (dept._depth || 0) * 2 }}>
                                                        {dept._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                                        {dept.node_name}{dept.level_type ? ` (${dept.level_type})` : ''}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    {formData.target_type === 'BRANCH' && (
                                        <FormControl fullWidth>
                                            <InputLabel>Select Branches</InputLabel>
                                            <Select multiple value={formData.target_ids}
                                                onChange={(e) => setFormData(prev => ({ ...prev, target_ids: e.target.value }))}
                                                input={<OutlinedInput label="Select Branches" />}
                                                sx={{ borderRadius: 2 }}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const branch = branches.find(b => b.id === value);
                                                            return <Chip key={value} label={branch ? branch.branch_name : value} size="small"
                                                                sx={{ bgcolor: '#ecfdf5', color: '#065f46' }} />;
                                                        })}
                                                    </Box>
                                                )}>
                                                {branches.map((branch) => (
                                                    <MenuItem key={branch.id} value={branch.id}>{branch.branch_name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Save Button */}
                        <Box display="flex" justifyContent="flex-end" pb={4}>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                onClick={handleSave}
                                disabled={loading}
                                sx={{
                                    bgcolor: 'primary.main',
                                    borderRadius: 2.5,
                                    px: 4, py: 1.4,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    fontSize: 16,
                                    boxShadow: (theme) => `0 4px 14px 0 ${theme.palette.primary.main}66`,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                {loading ? 'Saving...' : id ? 'Update Announcement' : 'Publish Announcement'}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Fade>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnnouncementEditor;
